/**
 * DrillExportModal - Modal for selecting drill export options
 * Allows users to choose date range and drill types to export
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Download, X, FileText, ChevronDown, Shield, AlertCircle } from 'lucide-react';
import { Input } from '../../../catalyst/input';
import { Checkbox } from '../../../catalyst/checkbox';
import { exportDrillsReportPDF, exportDrillComplianceReportPDF, isCompletionRecord } from '../../../../utils/pdf/drillExport';
import { exportDrillHistoryCSV } from '../../../../utils/drillExportCSV';
import { useDrillRegister } from '../../../../hooks/domain/useDrillRegister';
import { useDrillsData } from '../../../../hooks/domain/useDrillsData';

const PRESET_RANGES = [
  { id: 'this-month', label: 'This Month', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
  }},
  { id: 'last-month', label: 'Last Month', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start, end };
  }},
  { id: 'last-3-months', label: 'Last 3 Months', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
  }},
  { id: 'this-year', label: 'This Year', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), 11, 31);
    return { start, end };
  }},
  { id: 'last-year', label: 'Last Year', getValue: () => {
    const now = new Date();
    const start = new Date(now.getFullYear() - 1, 0, 1);
    const end = new Date(now.getFullYear() - 1, 11, 31);
    return { start, end };
  }},
  { id: 'all-time', label: 'All Time', getValue: () => {
    return { start: null, end: null };
  }}
];

const DrillExportModal = ({ isOpen, onClose, drills, vesselInfo }) => {
  const [selectedRange, setSelectedRange] = useState('this-month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(['all']);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [exportType, setExportType] = useState('records'); // 'records' or 'compliance'
  const [noDataMessage, setNoDataMessage] = useState('');
  
  // Get drill register and compliance data
  const { drillRegister, getDrillRegister } = useDrillRegister();
  const { getCompliancePercentage } = useDrillsData();

  // Get unique drill types from actual drills (completion records only)
  const availableDrillTypes = useMemo(() => {
    // FIX 2 (FT-468): limit to completion records so definitions don't pollute
    // the type list. FT-387: filter out type-less completions (undefined never enters Set).
    const types = new Set(
      drills.filter(isCompletionRecord).filter(d => d.type).map(d => d.type)
    );
    return Array.from(types).sort();
  }, [drills]);

  // Filter drills based on selection
  const filteredDrills = useMemo(() => {
    // FIX 2 (FT-468): strip definitions, archived, and deleted items FIRST.
    // The drills prop is a mixed array (definitions + completions + tombstones);
    // only real completion records should reach the count display, PDF, or CSV.
    // isCompletionRecord: requires `date`; excludes deleted_at/archived/drillType-only entries.
    let filtered = drills.filter(isCompletionRecord);

    // Apply date range filter
    if (selectedRange === 'custom' && customStart && customEnd) {
      const start = new Date(customStart);
      const end = new Date(customEnd);
      end.setHours(23, 59, 59); // Include the entire end day
      filtered = filtered.filter(d => {
        const drillDate = new Date(d.date);
        return drillDate >= start && drillDate <= end;
      });
    } else if (selectedRange !== 'all-time') {
      const preset = PRESET_RANGES.find(r => r.id === selectedRange);
      if (preset) {
        const { start, end } = preset.getValue();
        if (start && end) {
          filtered = filtered.filter(d => {
            const drillDate = new Date(d.date);
            return drillDate >= start && drillDate <= end;
          });
        }
      }
    }

    // Apply type filter
    if (!selectedTypes.includes('all')) {
      filtered = filtered.filter(d => selectedTypes.includes(d.type));
    }

    return filtered;
  }, [drills, selectedRange, customStart, customEnd, selectedTypes]);

  const handleTypeToggle = (type) => {
    if (type === 'all') {
      setSelectedTypes(['all']);
    } else {
      const newTypes = selectedTypes.includes(type)
        ? selectedTypes.filter(t => t !== type && t !== 'all')
        : [...selectedTypes.filter(t => t !== 'all'), type];
      
      setSelectedTypes(newTypes.length === 0 ? ['all'] : newTypes);
    }
  };

  const handleExport = () => {
    if (exportType === 'compliance') {
      // Export compliance report with drill register
      const register = getDrillRegister();
      const compliance = getCompliancePercentage();
      
      const exportOptions = {
        vesselName: vesselInfo?.name || 'Unknown Vessel',
        category: vesselInfo?.category || 'Unknown Category',
        vesselIMO: vesselInfo?.imo || 'Not Specified',
        reportTitle: 'Drill Compliance Report',
        compliancePercentage: compliance
      };

      const success = exportDrillComplianceReportPDF(register, drills, exportOptions);
      
      if (success) {
        onClose();
      }
    } else {
      // Export drill records report
      if (filteredDrills.length === 0) {
        setNoDataMessage('No drills found for the selected criteria. Try adjusting your filters or date range.');
        return;
      }

      // Clear any previous message
      setNoDataMessage('');

      // Get date range for report title
      let dateRangeText = '';
      if (selectedRange === 'custom') {
        dateRangeText = `${customStart} to ${customEnd}`;
      } else {
        const preset = PRESET_RANGES.find(r => r.id === selectedRange);
        dateRangeText = preset ? preset.label : '';
      }

      const exportOptions = {
        vesselName: vesselInfo?.name || 'Unknown Vessel',
        category: vesselInfo?.category || 'Unknown Category',
        vesselIMO: vesselInfo?.imo || 'Not Specified',
        reportTitle: `Safety Drills Report - ${dateRangeText}`,
        // FT-470 FIX: pass the raw array (or 'all') — not a joined string.
        // exportDrillsReportPDF uses drillType for header/filename only;
        // the drills passed here (filteredDrills) are already type-filtered.
        drillType: selectedTypes.includes('all') ? 'all' : selectedTypes
      };

      const success = exportDrillsReportPDF(filteredDrills, exportOptions);
      
      if (success) {
        onClose();
      }
    }
  };

  const handleExportCSV = () => {
    if (exportType === 'records') {
      if (filteredDrills.length === 0) {
        setNoDataMessage('No drills found for the selected criteria. Try adjusting your filters or date range.');
        return;
      }
      setNoDataMessage('');
      let dateRangeText = '';
      if (selectedRange === 'custom') {
        dateRangeText = `${customStart} to ${customEnd}`;
      } else {
        const preset = PRESET_RANGES.find(r => r.id === selectedRange);
        dateRangeText = preset ? preset.label : '';
      }
      exportDrillHistoryCSV(filteredDrills, {
        vesselName: vesselInfo?.name || 'Unknown Vessel',
        dateRangeText
      });
      onClose();
    }
    // Compliance CSV: out of scope for FT-468 (schedule-vs-actual shape is
    // awkward to flatten into a generic table; deferred to a follow-up ticket).
  };

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {

    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="bg-white rounded-t-lg border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-gray-400 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Export Drill Report</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Export Type Selection */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Export Type</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setExportType('records')}
                  className={`px-4 py-3 rounded-lg border text-left ${
                    exportType === 'records'
                      ? 'bg-cyan-50 border-cyan-500'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <FileText className={`h-5 w-5 mr-3 mt-0.5 ${
                      exportType === 'records' ? 'text-cyan-600' : 'text-gray-400'
                    }`} />
                    <div>
                      <p className={`font-medium ${
                        exportType === 'records' ? 'text-cyan-900' : 'text-gray-900'
                      }`}>
                        Drill Records
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Export completed drill records with date filtering
                      </p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => setExportType('compliance')}
                  className={`px-4 py-3 rounded-lg border text-left ${
                    exportType === 'compliance'
                      ? 'bg-cyan-50 border-cyan-500'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <Shield className={`h-5 w-5 mr-3 mt-0.5 ${
                      exportType === 'compliance' ? 'text-cyan-600' : 'text-gray-400'
                    }`} />
                    <div>
                      <p className={`font-medium ${
                        exportType === 'compliance' ? 'text-cyan-900' : 'text-gray-900'
                      }`}>
                        Compliance Report
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Export drill register with regulatory citations
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Date Range Selection - Only show for records export */}
            {exportType === 'records' && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Select Date Range</h3>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {PRESET_RANGES.map(range => (
                  <button
                    key={range.id}
                    onClick={() => {
                      setSelectedRange(range.id);
                      setShowCustomRange(false);
                    }}
                    className={`px-3 py-2 text-sm rounded-md border ${
                      selectedRange === range.id && !showCustomRange
                        ? 'bg-cyan-50 border-cyan-500 text-cyan-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => {
                  setShowCustomRange(!showCustomRange);
                  setSelectedRange('custom');
                }}
                className={`w-full px-3 py-2 text-sm rounded-md border flex items-center justify-between ${
                  showCustomRange
                    ? 'bg-cyan-50 border-cyan-500 text-cyan-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>Custom Date Range</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showCustomRange ? 'rotate-180' : ''}`} />
              </button>
              
              {showCustomRange && (
                <div className="mt-3 grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-md">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <Input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <Input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Drill Type Selection - Only show for records export */}
            {exportType === 'records' && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Select Drill Types</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Checkbox
                    checked={selectedTypes.includes('all')}
                    onChange={() => handleTypeToggle('all')}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-900">
                    All Drill Types
                  </label>
                </div>
                
                {availableDrillTypes.length > 0 && (
                  <div className="ml-6 space-y-2">
                    {availableDrillTypes.map(type => (
                      <div key={type} className="flex items-center">
                        <Checkbox
                          checked={selectedTypes.includes(type)}
                          onChange={() => handleTypeToggle(type)}
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          {(type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* No Data Warning */}
            {noDataMessage && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">No Data Available</p>
                    <p className="text-sm text-yellow-700 mt-1">{noDataMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                {exportType === 'compliance' ? (
                  <>
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Compliance Report Preview</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        The report will include:
                      </p>
                      <ul className="text-sm text-blue-700 mt-1 space-y-1">
                        <li>• Expected drill schedule from register</li>
                        <li>• Regulatory citations and requirements</li>
                        <li>• Equipment dependencies for each drill</li>
                        <li>• Current compliance percentage</li>
                        <li>• Recent drill performance history</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                    <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Export Preview</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        {filteredDrills.length} drill{filteredDrills.length !== 1 ? 's' : ''} will be exported
                        {selectedRange !== 'all-time' && ` for ${
                          selectedRange === 'custom' ? 'the selected period' : 
                          PRESET_RANGES.find(r => r.id === selectedRange)?.label.toLowerCase()
                        }`}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            {/* FT-468: CSV download — records export only (compliance CSV deferred) */}
            {exportType === 'records' && (
              <button
                data-testid="drills-export-csv"
                onClick={handleExportCSV}
                disabled={filteredDrills.length === 0}
                className={`px-4 py-2 text-sm font-medium rounded-md flex items-center border ${
                  filteredDrills.length > 0
                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </button>
            )}
            <button
              onClick={handleExport}
              disabled={exportType === 'records' && filteredDrills.length === 0}
              className={`px-4 py-2 text-sm font-medium rounded-md flex items-center ${
                (exportType === 'compliance' || filteredDrills.length > 0)
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportType === 'compliance'
                ? 'Export PDF'
                : `Export ${filteredDrills.length} Drill${filteredDrills.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default DrillExportModal;